import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';

import { logger } from '@/utils/logger';
export interface ERPApiResponse<T = any> {
  message: {
    status?: 'success' | 'error';
    success?: boolean;
    message?: string;
    data: T;
    pagination?: {
      page: number;
      page_size: number;
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  };
}

export interface PaginatedResult<T = any> {
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  } | null;
}

/**
 * Helper to check if API response is successful
 * Handles both old format (status: 'success') and new unified format (success: true)
 */
function isApiResponseSuccess(response: ERPApiResponse): boolean {
  return response.message.success === true || response.message.status === 'success';
}

/**
 * ERP Knowledge Base Service
 * Handles all knowledge base operations using Frappy ERP APIs
 */
class ERPKnowledgeBaseService {
  private cache: Map<string, { data: any; timestamp: number; expiry: number }> = new Map();
  private readonly DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Generic cached API call method
   */
  private async cachedApiCall<T>(
    cacheKey: string,
    apiCall: () => Promise<T>,
    cacheDuration = this.DEFAULT_CACHE_DURATION
  ): Promise<T> {
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() < cached.expiry) {
      return cached.data;
    }

    try {
      const result = await apiCall();

      // Cache the results
      this.cache.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
        expiry: Date.now() + cacheDuration
      });

      return result;
    } catch (error) {
      logger.error(`API call failed for ${cacheKey}:`, error);
      throw error;
    }
  }

  /**
   * Get folder contents (root folders if no parent_directory, or children of a specific folder)
   * @param parentDirectory - Optional parent folder name (e.g., 'KB-0001'). If omitted, returns root-level folders.
   * @param includeSubfolders - Include children_count for each folder (default: true)
   * @param page - Page number (default: 1)
   * @param pageSize - Items per page (default: 20)
   */
  async getFolderContents(parentDirectory?: string, includeSubfolders = true, page = 1, pageSize = 20): Promise<PaginatedResult> {
    const cacheKey = parentDirectory ? `folder_contents_${parentDirectory}_p${page}` : `folder_contents_root_p${page}`;

    return this.cachedApiCall(cacheKey, async () => {
      const params: Record<string, any> = {
        include_subfolders: includeSubfolders,
        page,
        page_size: pageSize,
      };
      if (parentDirectory) {
        params.parent_directory = parentDirectory;
      }

      const response = await axios.get<ERPApiResponse<any>>(
        getApiEndpoint('GET_FOLDER_CONTENTS'),
        {
          params,
          withCredentials: true
        }
      );

      if (isApiResponseSuccess(response.data)) {
        return {
          data: response.data.message.data || [],
          pagination: response.data.message.pagination || null,
        };
      } else {
        throw new Error(response.data.message.message || 'Failed to load folder contents');
      }
    });
  }

  /**
   * Create a knowledge base item (folder or file) using the unified endpoint
   * Uses multipart/form-data format
   */
  async createItem(data: {
    document_name: string;
    is_folder?: boolean;
    is_file?: boolean;
    parent_directory?: string;
    description?: string;
    file?: File;
  }): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('document_name', data.document_name);

      if (data.is_folder !== undefined) {
        formData.append('is_folder', data.is_folder ? '1' : '0');
      }
      if (data.is_file !== undefined) {
        formData.append('is_file', data.is_file ? '1' : '0');
      }
      if (data.parent_directory) {
        formData.append('parent_directory', data.parent_directory);
      }
      if (data.description) {
        formData.append('description', data.description);
      }
      if (data.file) {
        formData.append('file', data.file);
      }

      const response = await axios.post<ERPApiResponse<any>>(
        getApiEndpoint('CREATE_KNOWLEDGE_BASE_ITEM'),
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (isApiResponseSuccess(response.data)) {
        // Clear related cache entries
        this.clearCacheEntry('sections');
        this.clearCacheEntry('all_folders_nested');
        this.clearCacheByPattern('folder_contents_');
        this.clearCacheByPattern('categories_');
        this.clearCacheByPattern('subcategories_');

        return response.data.message.data;
      } else {
        throw new Error(response.data.message.message || 'Failed to create item');
      }
    } catch (error) {
      logger.error('Create item failed:', error);
      throw error;
    }
  }

  /**
   * Update a knowledge base item using the unified endpoint
   * Uses multipart/form-data format
   */
  async updateItem(data: {
    name: string;
    document_name?: string;
    description?: string;
    parent_directory?: string;
  }): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('name', data.name);

      if (data.document_name) {
        formData.append('document_name', data.document_name);
      }
      if (data.description !== undefined) {
        formData.append('description', data.description);
      }
      if (data.parent_directory) {
        formData.append('parent_directory', data.parent_directory);
      }

      const response = await axios.post<ERPApiResponse<any>>(
        getApiEndpoint('UPDATE_KNOWLEDGE_BASE_ITEM'),
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (isApiResponseSuccess(response.data)) {
        // Clear related cache entries
        this.clearCacheEntry('all_folders_nested');
        this.clearCacheByPattern('folder_contents_');
        this.clearCacheByPattern('item_');
        this.clearCacheByPattern('categories_');
        this.clearCacheByPattern('subcategories_');

        return response.data.message.data;
      } else {
        throw new Error(response.data.message.message || 'Failed to update item');
      }
    } catch (error) {
      logger.error('Update item failed:', error);
      throw error;
    }
  }

  /**
   * Delete a knowledge base item using the unified endpoint
   */
  async deleteItem(itemId: string): Promise<any> {
    try {
      const response = await axios.post<ERPApiResponse<any>>(
        getApiEndpoint('DELETE_KNOWLEDGE_BASE_ITEM'),
        { name: itemId },
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (isApiResponseSuccess(response.data)) {
        // Clear related cache entries
        this.clearCacheEntry('all_folders_nested');
        this.clearCacheByPattern('folder_contents_');
        this.clearCacheByPattern('item_');
        this.clearCacheByPattern('categories_');
        this.clearCacheByPattern('subcategories_');

        return response.data.message.data;
      } else {
        throw new Error(response.data.message.message || 'Failed to delete item');
      }
    } catch (error) {
      logger.error('Delete item failed:', error);
      throw error;
    }
  }

  /**
   * Create a new document in a subcategory
   */
  async createSubcategoryDocument(data: {
    subcategory_id: string;
    title: string;
    publish_content?: number;
    content?: string;
    attachment?: string;
    attachment_type?: string;
  }): Promise<any> {
    try {
      const response = await axios.post<ERPApiResponse<any>>(
        getApiEndpoint('CREATE_SUBCATEGORY_DOCUMENT'),
        data,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data.message.status === 'success') {
        // Clear related cache entries to ensure fresh data is loaded
        this.clearCacheByPattern('documents_');
        this.clearCacheByPattern('subcategories_');
        this.clearCacheByPattern('categories_');
        this.clearCacheEntry('sections');

        return response.data.message.data;
      } else {
        throw new Error(response.data.message.message);
      }
    } catch (error) {
      logger.error('Create subcategory document failed:', error);
      throw error;
    }
  }

  /**
   * Update an existing subcategory document
   */
  async updateSubcategoryDocument(data: {
    document_id: string;
    title?: string;
    content?: string;
    publish_content?: number;
    attachment?: string;
    attachment_type?: string;
  }): Promise<any> {
    try {
      const response = await axios.post<ERPApiResponse<any>>(
        getApiEndpoint('UPDATE_SUBCATEGORY_DOCUMENT'),
        data,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data.message.status === 'success') {
        // Clear related cache entries to ensure fresh data is loaded
        this.clearCacheByPattern('documents_');
        this.clearCacheByPattern('subcategories_');
        this.clearCacheByPattern('categories_');
        this.clearCacheEntry('sections');

        return response.data.message.data;
      } else {
        throw new Error(response.data.message.message);
      }
    } catch (error) {
      logger.error('Update subcategory document failed:', error);
      throw error;
    }
  }

  /**
   * Delete a subcategory document
   */
  async deleteSubcategoryDocument(documentId: string): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('document_id', documentId);

      const response = await axios.get<ERPApiResponse<any>>(
        `${getApiEndpoint('DELETE_SUBCATEGORY_DOCUMENT')}?${params}`,
        {
          withCredentials: true,
        }
      );

      if (response.data.message.status === 'success') {
        // Clear related cache entries to ensure fresh data is loaded
        this.clearCacheByPattern('documents_');
        this.clearCacheByPattern('subcategories_');
        this.clearCacheByPattern('categories_');
        this.clearCacheEntry('sections');

        return response.data.message.data;
      } else {
        throw new Error(response.data.message.message);
      }
    } catch (error) {
      logger.error('Delete subcategory document failed:', error);
      throw error;
    }
  }

  /**
   * Search for items by name or description
   * @param query - Search query (searches in document_name and description)
   * @param parentDirectory - Optional: Limit search to a specific folder
   * @param searchNested - If true, searches in all nested subfolders (default: true)
   * @param page - Page number (default: 1)
   * @param pageSize - Items per page, max 100 (default: 20)
   */
  async searchItems(
    query: string,
    parentDirectory?: string,
    searchNested = true,
    page = 1,
    pageSize = 20
  ): Promise<PaginatedResult> {
    // Don't cache search results
    try {
      const params: Record<string, any> = {
        query,
        search_nested: searchNested,
        page,
        page_size: pageSize,
      };
      if (parentDirectory) {
        params.parent_directory = parentDirectory;
      }

      const response = await axios.get<ERPApiResponse<any>>(
        getApiEndpoint('SEARCH_KNOWLEDGE_BASE_ITEMS'),
        {
          params,
          withCredentials: true
        }
      );

      if (isApiResponseSuccess(response.data)) {
        return {
          data: response.data.message.data || [],
          pagination: response.data.message.pagination || null,
        };
      } else {
        throw new Error(response.data.message.message || 'Search failed');
      }
    } catch (error) {
      logger.error('Search items failed:', error);
      throw error;
    }
  }

  // Cache management methods
  clearCache(): void {
    this.cache.clear();
  }

  clearCacheEntry(key: string): void {
    this.cache.delete(key);
  }

  clearCacheByPattern(pattern: string): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => key.includes(pattern));
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  getCacheStatus() {
    const entries = Array.from(this.cache.entries()).map(([key, value]) => ({
      key,
      timestamp: value.timestamp,
      expiry: value.expiry,
      isExpired: Date.now() >= value.expiry,
      timeUntilExpiry: Math.max(0, value.expiry - Date.now())
    }));

    return {
      totalEntries: this.cache.size,
      entries
    };
  }
}

export const erpKnowledgeBaseService = new ERPKnowledgeBaseService();